import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditProjectFilterModal from 'components/admin/common/modal/filter/EditProjectFilterModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';

const ProjectFilter = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createProjectFilterData,
        postError: creatingError,
        postLoading: creating,
        putData: updateProjectFilterData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteProjectFilterData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.PROJECT_FILTERS);

    const { fetchData: filterOptionsData } = useCrud(API_ENDPOINTS.FILTER_OPTIONS);
    const { fetchData: projectData } = useCrud(API_ENDPOINTS.PROJECTS);
    const { fetchData: filterData } = useCrud(API_ENDPOINTS.FILTERS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteProjectFilter = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditProjectFilter = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteProjectFilter, modalData: projectFilterToDelete, openModal: openDeleteProjectFilter, closeModal: closedDeleteProjectFilter } = useModalState({
        onClose: handleCloseDeleteProjectFilter
    });

    const { isOpen: isOpenEditProjectFilter, modalData: selectedProjectFilter, openModal: openEditProjectFilter, closeModal: closedEditProjectFilter } = useModalState({
        onClose: handleCloseEditProjectFilter
    });

    const validationRules = [
        { field: 'project_id', method: 'required' },
        { field: 'filter_option_id', method: 'required' }
    ];

    const handleModalSave = useCallback(async (updatedProjectFilter) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedProjectFilter, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (updatedProjectFilter.id) {
                await updateProjectFilterData(updatedProjectFilter.id, updatedProjectFilter);
                setSuccessMessage("Filtro de proyecto actualizado con éxito.");
            } else {
                await createProjectFilterData(updatedProjectFilter);
                setSuccessMessage("Filtro de proyecto creado con éxito.");
            }
            closedEditProjectFilter();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateProjectFilterData, createProjectFilterData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newProjectFilter = {
            project_id: '',
            filter_option_id: ''
        };
        openEditProjectFilter(newProjectFilter);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (projectFilterToDelete) {
            try {
                await deleteProjectFilterData(projectFilterToDelete.id);
                setSuccessMessage("Filtro de proyecto eliminado con éxito.");
                refetch();
                closedDeleteProjectFilter();
            } catch (error) {
                console.error(error);
            }
        }
    }, [projectFilterToDelete, deleteProjectFilterData, refetch]);

    const columns = useMemo(() => [
        { field: 'project_title', header: 'Proyecto', sortable: true },
        { field: 'filter_name', header: 'Filtro', sortable: true },
        { field: 'filter_option_value', header: 'Valor del Filtro', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Filtros de Proyecto</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Filtro de Proyecto
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditProjectFilter}
                onDelete={openDeleteProjectFilter}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="project_id"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditProjectFilterModal
                show={isOpenEditProjectFilter}
                projectFilter={selectedProjectFilter}
                handleClose={closedEditProjectFilter}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
                products={projectData}
                filters={filterData}
                filtersOptions={filterOptionsData}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteProjectFilter}
                onHide={closedDeleteProjectFilter}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(ProjectFilter);